import { axiosInstance } from "@retro-ia/shared/axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export interface ActividadInfo {
  id: number;
  nombre: string;
}

export interface AprendizConActividades {
  id: number;
  nombre: string;
  username: string;
  correo: string;
  actividades: Array<{
    id: number;
    idActividad: number;
    nombre: string;
    asignadoEl: Date;
    intentos: number;
  }>;
}

export interface ResumenDistribucion {
  total: number;
  sinAsignar: number;
  porActividad: Record<number, number>;
}

export interface AprendicesConActividadesResponse {
  aprendices: AprendizConActividades[];
  actividades: ActividadInfo[];
  resumen: ResumenDistribucion;
}

export interface AsignacionMasivaVariables {
  idPrograma: number;
  aprendicesIds: number[];
  actividadesIds: number[];
  modo?: "replace" | "add";
}

export interface AsignacionMasivaResponse {
  asignados: number;
  modificados: number;
  desasignados: number;
  errores: string[];
}

/**
 * Hook para obtener aprendices de un programa con sus actividades asignadas
 */
export function useAprendicesConActividades(
  idPrograma: number | undefined,
  enabled = true,
) {
  return useQuery<AprendicesConActividadesResponse, Error>({
    queryKey: ["aprendices-con-actividades", idPrograma],
    queryFn: async () => {
      if (!idPrograma) throw new Error("idPrograma is required");
      const response = await axiosInstance.get(
        `/actividades-programa/programas/${idPrograma}/aprendices-con-actividades`,
      );
      return response.data;
    },
    enabled: !!idPrograma && enabled,
    staleTime: 1 * 60 * 1000, // 1 minuto
  });
}

/**
 * Hook para asignación masiva de aprendices a actividades
 */
export function useAsignacionMasiva() {
  const queryClient = useQueryClient();

  return useMutation<
    AsignacionMasivaResponse,
    Error,
    AsignacionMasivaVariables
  >({
    mutationFn: async ({
      idPrograma,
      aprendicesIds,
      actividadesIds,
      modo = "replace",
    }: AsignacionMasivaVariables) => {
      const response = await axiosInstance.post<AsignacionMasivaResponse>(
        `/actividades-programa/programas/${idPrograma}/asignar-masivo`,
        {
          aprendicesIds,
          actividadesIds,
          modo,
        },
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["aprendices-con-actividades", variables.idPrograma],
      });
      queryClient.invalidateQueries({
        queryKey: ["actividad-aprendices"],
      });
      queryClient.invalidateQueries({
        queryKey: ["clientesFinalesProgramas"],
      });
      queryClient.invalidateQueries({
        queryKey: ["programas-dashboard-admin"],
      });
    },
  });
}

/**
 * Hook para remover aprendices de un programa (soft delete)
 */
export function useRemoveAprendicesFromPrograma(
  idPrograma: number,
  options?: { onSuccess?: () => void },
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (aprendicesIds: number[]) => {
      await Promise.all(
        aprendicesIds.map((idAprendiz) =>
          axiosInstance.put(
            `/aprendices-programas/programa/${idPrograma}/aprendiz/${idAprendiz}`,
          ),
        ),
      );
    },
    onSuccess: () => {
      const count = 1; // optimistic; real count handled by caller
      toast.success("Aprendiz(ces) removido(s) del programa correctamente");
      queryClient.invalidateQueries({
        queryKey: ["aprendices-con-actividades", idPrograma],
      });
      options?.onSuccess?.();
      void count;
    },
    onError: () => {
      toast.error("Error al remover aprendiz(ces) del programa");
    },
  });
}
